import { RequestAbortSignal } from '@/Enums/ChangeLogHistory/RequestAbortSignal.enum';
import FilterDate from '@/Enums/FilterDate.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import RefreshButton from '@/components/Buttons/RefreshButton';
import DateRangeWithShortcuts from '@/components/DateRangeWithShortcuts';
import UploadIcon from '@/components/Icons/UploadIcon';
import SearchInput from '@/components/SearchInput';
import { getShortcutItemsInclusive } from '@/helpers/datePicker/shortcuts';
import { useRequestSaver } from '@/hooks/RequestSaver';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectChangeLogHistoryFilters } from '@/store/selectors/changeLogHistory.selectors';
import { exportLogHistory, getLogHistory, setFilters } from '@/store/slices/changeLogHistory.slice';
import { Button, inputBaseClasses } from '@mui/material';
import { GridToolbarContainer } from '@mui/x-data-grid-pro';
import { DateRange } from '@mui/x-date-pickers-pro';
import debounce from 'debounce';
import moment, { Moment } from 'moment';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import FilterPopover from './FilterPopover';

const Toolbar = () => {
  const dispatch = useAppDispatch();
  const requestSaver = useRequestSaver();

  const onExportClick = () => {
    dispatch(
      exportLogHistory({ ...requestSaver.requestSaverSetCancellation(RequestAbortSignal.ExportChangeLogHistory) }),
    );
  };

  const onRefreshClick = () => {
    dispatch(getLogHistory({ ...requestSaver.requestSaverSetCancellation(RequestAbortSignal.ChangeLogHistory) }));
  };

  const { startDate, endDate } = useAppSelector(selectChangeLogHistoryFilters);

  const [search, setSearch] = useState<string>('');

  const updateSearchFilter = useCallback(
    debounce((newSearch: string) => {
      dispatch(setFilters({ search: newSearch }));
    }, 500),
    [dispatch],
  );

  const onSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newSearch = event.target?.value;
      setSearch(newSearch);
      updateSearchFilter(newSearch);
    },
    [updateSearchFilter, setSearch],
  );

  const dateRangeValue: DateRange<Moment> = useMemo(() => [moment(startDate), moment(endDate)], [startDate, endDate]);

  const onDateRangeChange = useCallback(
    (range: DateRange<Moment>) => {
      const newStartDate = range[0]?.format(DateFormat.Iso8601);
      const newEndDate = range[1]?.format(DateFormat.Iso8601);
      if (startDate !== newStartDate || endDate !== newEndDate) {
        dispatch(
          setFilters({
            startDate: newStartDate,
            endDate: newEndDate,
          }),
        );
      }
    },
    [setFilters, startDate, endDate],
  );

  return (
    <GridToolbarContainer sx={{ padding: 0 }}>
      <div className='p-[20px] gap-3 relative flex justify-between w-full'>
        <div className='table-controls flex flex-row gap-[10px]'>
          <SearchInput placeholder='Search..' value={search} onChange={onSearchChange} style={{ height: 'auto' }} />
          <DateRangeWithShortcuts
            value={dateRangeValue}
            fullWidth
            disableFuture
            dateRangeFormat={FilterDate.CUSTOM_RANGE}
            onChange={onDateRangeChange}
            shortcuts={getShortcutItemsInclusive()}
            inputProps={{
              slotProps: {
                input: {
                  sx: {
                    [`& .${inputBaseClasses.input}`]: {
                      padding: '7px 8px 7px 2px',
                    },
                    minWidth: 290,
                  },
                },
              },
            }}
          />
          <FilterPopover />
        </div>
        <div className='flex flex-row gap-[10px]'>
          <RefreshButton onClick={onRefreshClick} />
          <Button variant='contained' onClick={onExportClick} sx={{ gap: 1 }}>
            <UploadIcon />
            <span>Export</span>
          </Button>
        </div>
      </div>
    </GridToolbarContainer>
  );
};

export default Toolbar;
